import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StoryBeat, StoryBoard, StoryBriefState } from "./types";
import {
  requestStoryRender, requestStoryReplan, requestStoryDiscard,
  useStoryBoardPatch, useStoryCommit,
} from "./studioBus";
import { usePayJob } from "@/components/pay/useResumeJob";
import SiteMarkSwitch from "@/components/pay/SiteMarkSwitch";

// StoryBriefCard — the approval gate, built to CapCut's brief-node blueprint.
//
// Structure and metrics come from CapCut's own stylesheet (the page capture the
// user supplied), so this reads as the same kind of object:
//   .briefRenderer  width 376, min-height 376, padding 20, gap 16, flex column
//   .section        flex column, gap 12
//   .rowContainer   flex row, wrap, gap 8, align center      <- label + pill
//   .briefButton    height 24, radius 6, max-width 156, subtle fill
// 376 is CapCut's module width — every node (brief, loading, player) matches it.
//
// CONTENT is ours and only ours. CapCut shows Visual style / Platform / Aspect
// pills because their engine consumes them; ours consumes narrator, tone,
// narration text, and an optional music bed, so those are the only controls.
// What our engine hardcodes is stated as a FACT, never as a pill implying a
// choice it can't honour.
//
// Nothing has rendered and nothing is charged until Generate is pressed.

const C = {
  // Dark-side equivalents of CapCut's light tokens (this console is dark).
  cardBg: "#0e141d",
  border: "#223046",
  fill: "rgba(255,255,255,.06)",
  textPrimary: "#e9eef5",
  textSecondary: "#b6c2d4",
  textTertiary: "rgba(220,225,240,.7)",
  cyan: "#38bdf8",
  pink: "#38bdf8",
  purple: "#0ea5e9",
  green: "#34d399",
  gold: "#e3b341",
  muted: "#7c8aa0",
};

// The seven voices story_engine.py ships (its VOICES list). Kokoro speaks only
// these — the list is not decorative.
const VOICES: Array<{ id: string; name: string; noteKey: string }> = [
  { id: "af_heart", name: "Heart", noteKey: "studio.voiceNotes.warm" },
  { id: "af_bella", name: "Bella", noteKey: "studio.voiceNotes.bright" },
  { id: "af_nicole", name: "Nicole", noteKey: "studio.voiceNotes.soft" },
  { id: "am_adam", name: "Adam", noteKey: "studio.voiceNotes.steady" },
  { id: "am_michael", name: "Michael", noteKey: "studio.voiceNotes.deep" },
  { id: "bf_emma", name: "Emma", noteKey: "studio.voiceNotes.uk" },
  { id: "bm_george", name: "George", noteKey: "studio.voiceNotes.uk" },
];

// Wire values — story_engine.py keys on these exact strings, so only the
// display label goes through t().
const TONES = ["educational", "inspirational", "viral"];

/** CapCut .keyElementsTitle — 700 weight, -4px bottom margin. */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ color: C.textPrimary, fontWeight: 700, fontSize: 14, letterSpacing: ".094px", lineHeight: "150%", marginBottom: -4 }}>
      {children}
    </div>
  );
}

/** CapCut .rowContainerElement — label + control on one wrapping row. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ alignItems: "center", display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
      <div style={{ color: C.textTertiary, fontSize: 14, letterSpacing: ".094px", lineHeight: "150%", minWidth: 92 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

/** CapCut .briefButton — a 24px pill. Ours wraps a native select so it stays
 *  keyboard-accessible instead of reimplementing their popover. */
function PillSelect({
  value, onChange, disabled, options, testId,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  options: Array<{ id: string; label: string }>;
  testId?: string;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: C.fill,
        border: "none",
        borderRadius: 6,
        color: C.textPrimary,
        cursor: disabled ? "default" : "pointer",
        fontSize: 12,
        fontWeight: 500,
        height: 24,
        maxWidth: 156,
        padding: "0 6px",
        transition: "all .2s ease",
      }}
    >
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );
}

/** A value the engine hardcodes — flat, so it never reads as a control. */
function FactPill({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ alignItems: "center", color: C.textSecondary, display: "inline-flex", fontSize: 12, fontWeight: 500, height: 24 }}>
      {children}
    </span>
  );
}

/** The route's and the worker's limits (routes/story.js checkBoard): more is refused there, so the card stops first. */
export const MAX_BEATS = 12;
export const MAX_NARRATION_CHARS = 6000;

function estimate(beats: StoryBeat[]): number {
  // Mirrors story_engine.estimate_seconds — ~150 wpm + 0.35s pause per beat.
  const words = beats.reduce((n, b) => n + b.narration.trim().split(/\s+/).filter(Boolean).length, 0);
  return Math.round((words / 2.5 + 0.35 * beats.length) * 10) / 10;
}

export default function StoryBriefCard({ brief }: { brief: StoryBriefState }) {
  const { t } = useTranslation();
  const [board, setBoard] = useState<StoryBoard>(brief.board);
  const [music, setMusic] = useState<File | null>(null);
  const [openBeat, setOpenBeat] = useState<number | null>(null);
  const musicRef = useRef<HTMLInputElement>(null);

  // A re-plan replaces the board wholesale — adopt it, drop stale edits.
  useEffect(() => { setBoard(brief.board); }, [brief.board]);

  const locked = !!brief.rendering;
  const live = board.beats.filter((b) => b.narration.trim());
  const seconds = estimate(live);
  const cost = brief.renderCostCredits;
  const chars = live.reduce((n, b) => n + b.narration.trim().length, 0);
  const tooMany = live.length > MAX_BEATS;
  const tooLong = chars > MAX_NARRATION_CHARS;
  const atMax = board.beats.length >= MAX_BEATS;
  const canRender = !locked && live.length > 0 && !tooMany && !tooLong;

  // Chat quick-actions nudge one field; they merge here so they can never wipe
  // narration the user already rewrote.
  useStoryBoardPatch(useCallback((patch: Partial<StoryBoard>) => {
    if (locked) return;
    setBoard((p) => ({ ...p, ...patch }));
  }, [locked]));

  // The pay sheet keeps the board on the card through checkout (a music file
  // cannot ride along, so the flow asks for it again).
  usePayJob("tiktok", () => ({ payload: { board: { ...board, beats: board.beats.filter((b) => b.narration.trim()) }, hadMusic: !!music } }));
  // "Start generation" from chat commits whatever is on the card right now —
  // including its music file, which only the card holds.
  useStoryCommit(useCallback(() => {
    if (locked) return;
    const beats = board.beats.filter((b) => b.narration.trim());
    if (!beats.length || beats.length > MAX_BEATS) return;
    if (beats.reduce((n, b) => n + b.narration.trim().length, 0) > MAX_NARRATION_CHARS) return;
    requestStoryRender({ ...board, beats }, music);
  }, [locked, board, music]));

  const patch = (i: number, next: Partial<StoryBeat>) => setBoard((p) => ({ ...p, beats: p.beats.map((b, n) => (n === i ? { ...b, ...next } : b)) }));
  const remove = (i: number) => setBoard((p) => ({ ...p, beats: p.beats.filter((_, n) => n !== i) }));
  const add = () => setBoard((p) => (p.beats.length >= MAX_BEATS ? p : { ...p, beats: [...p.beats, { narration: "", query: "", image_prompt: "" }] }));

  return (
    <div
      data-testid="story-brief-card" className="nodrag nowheel" style={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minHeight: 376,
        padding: 20,
        position: "relative",
        width: 376,
        background: C.cardBg,
        color: C.textPrimary,
        fontFamily: "system-ui, sans-serif",
      }}
    >
      {/* ---- Summary ---- */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionTitle>{t("studio.brief.summary")}</SectionTitle>
        <div style={{ fontSize: 12, lineHeight: 1.6, color: C.textTertiary }}>{t("studio.brief.planLead")} <b style={{ color: C.textPrimary }}>{t("studio.brief.planBold")}</b>
          {" "}{t("studio.brief.planTail")}
        </div>
      </div>

      {/* ---- Key elements — ONLY what the engine consumes ---- */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionTitle>{t("studio.brief.keyElements")}</SectionTitle>

        <Row label={t("studio.brief.narrator")}>
          <PillSelect testId="brief-voice" value={board.voice} disabled={locked}
            onChange={(v) => setBoard((p) => ({ ...p, voice: v }))} options={VOICES.map((v) => ({ id: v.id, label: `${v.name} · ${t(v.noteKey)}` }))} />
        </Row>

        <Row label={t("studio.brief.tone")}>
          <PillSelect testId="brief-tone" value={TONES.includes(board.tone) ? board.tone : TONES[1]} disabled={locked}
            onChange={(v) => setBoard((p) => ({ ...p, tone: v }))} options={TONES.map((id) => ({ id, label: t(`studio.brief.tones.${id}`) }))} />
        </Row>

        <Row label={t("studio.brief.music")}>
          {music ? (
            <span style={{ alignItems: "center", display: "inline-flex", gap: 6, height: 24, maxWidth: 156 }}>
              <span style={{ color: C.green, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {music.name}
              </span>
              <button type="button" disabled={locked} onClick={() => setMusic(null)}
                style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, textDecoration: "underline" }}>{t("studio.brief.remove")}
              </button>
            </span>
          ) : (
            <>
              <button type="button" disabled={locked} data-testid="brief-music" onClick={() => musicRef.current?.click()}
                style={{ background: C.fill, border: "none", borderRadius: 6, color: C.textSecondary, cursor: "pointer", fontSize: 12, fontWeight: 500, height: 24, padding: "0 8px" }}>
                {t("studio.brief.addTrack")}
              </button>
              <input ref={musicRef} type="file" data-consent-tool="director" accept="audio/*" style={{ display: "none" }}
                onChange={(e) => setMusic(e.target.files?.[0] || null)} />
            </>
          )}
        </Row>

        {/* Facts — the engine hardcodes these. Flat, not pills. */}
        <Row label={t("studio.brief.visuals")}><FactPill>{t("studio.brief.visualsValue")}</FactPill></Row>
        <Row label={t("studio.brief.captions")}><FactPill>{t("studio.brief.captionsValue")}</FactPill></Row>
        <Row label={t("studio.brief.format")}><FactPill>1080×1920 · 9:16</FactPill></Row>
        <Row label={t("studio.brief.length")}>
          <FactPill>{t("studio.brief.lengthValue", { count: live.length, seconds })}<span style={{ color: C.muted, marginLeft: 4 }}>{t("studio.brief.estimated")}</span></FactPill>
        </Row>
      </div>

      {/* ---- Narration ---- */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <SectionTitle>{t("studio.brief.narration")}</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {board.beats.map((beat, i) => {
            const open = openBeat === i;
            return (
              <div key={i} data-testid={`brief-beat-${i}`}
                style={{ background: C.fill, border: `1px solid ${open ? C.cyan : "transparent"}`, borderRadius: 8, padding: 8 }}>
                <div style={{ alignItems: "center", display: "flex", gap: 8, marginBottom: 6 }}>
                  <span style={{ color: C.muted, fontSize: 12, fontWeight: 700 }}>{String(i + 1).padStart(2, "0")}</span>
                  <button type="button" onClick={() => setOpenBeat(open ? null : i)}
                    style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, marginLeft: "auto" }}>
                    {open ? t("studio.brief.hideVisual") : t("studio.brief.showVisual")}
                  </button>
                  <button type="button" disabled={locked} onClick={() => remove(i)} data-testid={`brief-beat-remove-${i}`}
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, color: C.muted, cursor: "pointer", fontSize: 12, padding: "1px 6px" }}>
                    ✕
                  </button>
                </div>
                <textarea value={beat.narration} disabled={locked} rows={2}
                  onChange={(e) => patch(i, { narration: e.target.value })}
                  placeholder={t("studio.brief.narrationPlaceholder")} style={{ background: "rgba(0,0,0,.25)", border: `1px solid ${C.border}`, borderRadius: 6, boxSizing: "border-box", color: C.textPrimary, fontSize: 13, lineHeight: "160%", padding: "8px 10px", resize: "vertical", width: "100%" }} />
                {open && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
                    <div>
                      <div style={{ color: C.muted, fontSize: 12, marginBottom: 2 }}>{t("studio.brief.stockSearch")}</div>
                      <input value={beat.query} disabled={locked} onChange={(e) => patch(i, { query: e.target.value })}
                        placeholder={t("studio.brief.stockPlaceholder")} style={{ background: "rgba(0,0,0,.25)", border: `1px solid ${C.border}`, borderRadius: 6, boxSizing: "border-box", color: C.textPrimary, fontSize: 12, padding: "6px 8px", width: "100%" }} />
                    </div>
                    <div>
                      <div style={{ color: C.muted, fontSize: 12, marginBottom: 2 }}>{t("studio.brief.aiImage")}</div>
                      <input value={beat.image_prompt} disabled={locked} onChange={(e) => patch(i, { image_prompt: e.target.value })}
                        placeholder={t("studio.brief.aiImagePlaceholder")} style={{ background: "rgba(0,0,0,.25)", border: `1px solid ${C.border}`, borderRadius: 6, boxSizing: "border-box", color: C.textPrimary, fontSize: 12, padding: "6px 8px", width: "100%" }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!locked && (
          <button type="button" onClick={add} disabled={atMax} aria-disabled={atMax || undefined} data-testid="brief-beat-add" style={{ alignSelf: "flex-start", background: "none", border: `1px dashed ${C.border}`, borderRadius: 8, color: C.muted, cursor: atMax ? "default" : "pointer", fontSize: 12, opacity: atMax ? 0.6 : 1, padding: "6px 12px" }}>
            {t("studio.brief.addBeat")}
          </button>
        )}
        {(atMax || tooMany) && (
          <p data-testid="brief-limit-beats" style={{ color: C.muted, fontSize: 12, margin: 0 }}>{t("studio.brief.maxBeats", { count: MAX_BEATS })}</p>
        )}
        {tooLong && (
          <p data-testid="brief-limit-chars" role="alert" style={{ color: C.pink, fontSize: 12, margin: 0 }}>{t("studio.brief.tooLong", { chars: chars.toLocaleString(), max: MAX_NARRATION_CHARS.toLocaleString() })}</p>
        )}
      </div>

      {/* ---- Commit ---- */}
      <SiteMarkSwitch testId="brief-site-mark" disabled={locked} style={{ color: C.textSecondary, marginTop: "auto" }} />
      <div style={{ alignItems: "center", borderTop: `1px solid ${C.border}`, display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 12 }}>
        <button type="button" data-testid="brief-generate" disabled={!canRender}
          onClick={() => requestStoryRender({ ...board, beats: live }, music)}
          style={{
            background: !canRender ? "rgba(255,255,255,.08)" : `linear-gradient(135deg,${C.pink},${C.purple})`,
            border: "none", borderRadius: 10,
            color: !canRender ? C.muted : "#fff",
            cursor: !canRender ? "default" : "pointer",
            fontSize: 13, fontWeight: 700, letterSpacing: .3, padding: "10px 16px",
          }}>
          {locked ? t("studio.brief.rendering") : t("studio.brief.generateVideo")}
          {cost !== null && !locked ? t("studio.brief.costSuffix", { cost: cost.toLocaleString() }) : ""}
        </button>
        <button type="button" disabled={locked} onClick={() => requestStoryReplan()} data-testid="brief-replan" style={{ background: C.fill, border: "none", borderRadius: 8, color: C.textSecondary, cursor: locked ? "default" : "pointer", fontSize: 12, padding: "9px 12px" }}>
          {t("studio.brief.planAgain")}
        </button>
        <button type="button" disabled={locked} onClick={() => requestStoryDiscard()} data-testid="brief-discard" style={{ background: "none", border: "none", color: C.muted, cursor: locked ? "default" : "pointer", fontSize: 12, textDecoration: "underline" }}>{t("studio.brief.discard")}
        </button>
        {!live.length && <span style={{ color: C.gold, fontSize: 13 }}>{t("studio.brief.allEmpty")}</span>}
      </div>
    </div>
  );
}
